import {Box, Button, Typography} from '@mui/material';
import {t} from 'i18next';
import {useEffect, useState} from 'react';
import AurButton from '../../../common/aur-button/AurButton';
import AurDrawer from '../../../common/aur-drawer/AurDrawer';
import AurTabMenu, {AurTab} from '../../../common/aur-tab-menu/AurTabMenu';
import {
  ArrayItemType,
  SortItemType,
} from '../../../eca-doc-list/eca-doc-list.util';
import ManageColFields from '../../../eca-doc-list/manage-fields-sort/ManageColFields';
import ManageSortOrder from '../../../eca-doc-list/manage-fields-sort/ManageSortOrder';
import {useAnalytics} from '../../../../hooks/useAnalytics';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../../constants/analyticsConstants';

const MAX_SORT_COLUMNS = 5;

export interface IColumn {
  name: string;
  label: string;
  isVisible: boolean;
  isSortable: boolean;
  ordinal?: number;
  order?: 'asc' | 'desc';
}

export interface IManageColumnsDrawerProps {
  showDrawer: boolean;
  columns: IColumn[];
  onClose?: () => void;
  onSave?: (selectedColumns: IColumn[], sortedColumns: IColumn[]) => void;
}

const toArrayItemType = (columns: IColumn[]): ArrayItemType[] => {
  const arrayItemTypes: ArrayItemType[] = [];

  for (const column of columns) {
    arrayItemTypes.push({
      id: column.name,
      name: column.name,
      label: column.label,
      dataType: '',
    });
  }

  return arrayItemTypes;
};

const toSortItemType = (columns: IColumn[]): SortItemType[] => {
  const sortItemTypes: SortItemType[] = [];

  let index = 0;
  for (const column of columns) {
    sortItemTypes.push({
      fieldValue: {
        id: column.name,
        name: column.name,
        label: column.label,
        dataType: '',
      },
      sortValue: {
        id: column.order === 'asc' ? '1' : '2',
        name: column?.order ?? 'null',
        label: column.order?.toUpperCase() ?? 'null',
        dataType: '',
      },
      id: index++,
    });
  }

  return sortItemTypes;
};

const ManageColumnsDrawer: React.FC<IManageColumnsDrawerProps> = ({
  showDrawer,
  columns,
  onClose,
  onSave,
}) => {
  const {trackGAEvent} = useAnalytics();

  const [isComponentLoaded, setIsComponentLoaded] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState<IColumn[]>([]);
  const [sortedColumns, setSortedColumns] = useState<IColumn[]>([]);

  const [selectedList, setSelectedList] = useState<ArrayItemType[]>([]);
  const [sortOrder, setSortOrder] = useState<SortItemType[]>([]);

  useEffect(() => {
    // Initialize Selected Columns
    const selectedColumns = columns.filter((i) => i.isVisible);
    setSelectedColumns(selectedColumns);
    setSelectedList(toArrayItemType(selectedColumns));

    // Initialize Sorted Columns
    const sortedColumns = columns
      .filter((i) => i.isSortable)
      .filter((i) => (i.ordinal ?? -1) >= 0);
    sortedColumns.sort(
      (a: IColumn, b: IColumn) => (a.ordinal ?? -1) - (b.ordinal ?? -1)
    );
    setSortedColumns(sortedColumns);
    setSortOrder(toSortItemType(sortedColumns));

    setIsComponentLoaded(true);
  }, []);

  const getSortableColumns = (): ArrayItemType[] => {
    const sortableColumns = selectedColumns.filter((i) => i.isSortable);
    return toArrayItemType(sortableColumns);
  };

  const handleSelectionChange = (arrayItemTypes: ArrayItemType[]) => {
    const iColumns: IColumn[] = [];

    for (const arrayItemType of arrayItemTypes) {
      const column = columns.find((i) => i.name === arrayItemType.name)!;
      iColumns.push({
        ...column,
        isVisible: true,
      });
    }

    setSelectedColumns(iColumns);
  };

  const handleSortChange = (sortItemTypes: SortItemType[]) => {
    const iColumns: IColumn[] = [];

    let index = 0;
    for (const sortItemType of sortItemTypes) {
      const name = sortItemType.fieldValue.name;
      const direction = sortItemType.sortValue.name;
      const column = columns.find((i) => i.name === name);
      if (column) {
        iColumns.push({
          ...column,
          isSortable: true,
          ordinal: index++,
          order: direction === 'asc' ? 'asc' : 'desc',
        });
      }
    }

    setSortedColumns(iColumns);
  };

  const tabs: AurTab[] = [
    {
      header: {
        text: t('indexSearchPage.aurTabControl.fields'),
      },
      content: (
        <>
          {isComponentLoaded && (
            <ManageColFields
              selectedList={selectedList}
              availableList={toArrayItemType(columns)}
              getSelectedList={setSelectedList}
            />
          )}
        </>
      ),
      index: 0,
    },
    {
      header: {
        text: t('indexSearchPage.aurTabControl.sortOrder'),
      },
      content: (
        <>
          {isComponentLoaded && (
            <ManageSortOrder
              selectedList={getSortableColumns()}
              sortOrder={sortOrder}
              getSortOrder={setSortOrder}
              includeSortOrderInEventName
            />
          )}
        </>
      ),
      index: 1,
    },
  ];

  useEffect(() => {
    if (!isComponentLoaded) {
      return;
    }

    const selectedColumnCount = selectedColumns.filter(
      (i) => i.isSortable
    ).length;
    const totalSortableColumns =
      selectedColumnCount < MAX_SORT_COLUMNS
        ? selectedColumnCount
        : MAX_SORT_COLUMNS;
    const remainingSortAvailable = totalSortableColumns - sortedColumns.length;

    const updatedSortOrder = [...toSortItemType(sortedColumns)];
    if (remainingSortAvailable > 0) {
      // Add some items to the collection
      for (let i = 0; i < remainingSortAvailable; i++) {
        updatedSortOrder.push({
          fieldValue: {
            label: 'Choose',
            id: '0',
            name: 'choose',
            dataType: '',
          },
          sortValue: {
            label: 'ASC',
            id: '1',
            name: 'asc',
            dataType: '',
          },
          id: sortOrder.length + i,
        });
      }
    } else if (remainingSortAvailable == 0) {
      // Do nothing
    } else {
      // Remove some items from the collection
      updatedSortOrder.splice(totalSortableColumns);
    }

    setSortOrder(updatedSortOrder);
  }, [selectedColumns, isComponentLoaded]);

  useEffect(() => {
    if (!isComponentLoaded) {
      return;
    }
    handleSelectionChange(selectedList);
  }, [selectedList, isComponentLoaded]);

  useEffect(() => {
    if (!isComponentLoaded) {
      return;
    }
    handleSortChange(sortOrder);
  }, [sortOrder, isComponentLoaded]);

  const handleClose = () => onClose?.();

  const handleSave = () => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.projectViewTab.saveManageColumnAndSortOrder'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.DRAWER,
        action: t('gaTracking.projectViewTab.savedSettings'),
      },
      subPage: {
        subpage_name: t('gaTracking.projectViewTab.manageColumnsAndSortOrder'),
        subpage_type: GA_SUBPAGE_TYPE.DRAWER,
      },
    });
    onSave?.(selectedColumns, sortedColumns);
  };

  const handleTabChange = (tabIndex: number) => {
    if (tabIndex === 0 || tabIndex === 1) {
      trackGAEvent({
        eventName: GA_EVENT_NAMES.CLICK,
        click: {
          name:
            tabIndex === 0
              ? t('gaTracking.projectViewTab.fields')
              : t('gaTracking.projectViewTab.sortOrder'),
          type: GA_CLICK_TYPES.TAB,
          region: GA_REGIONS.DRAWER,
          action:
            tabIndex === 0
              ? t('gaTracking.projectViewTab.tabFields')
              : t('gaTracking.projectViewTab.tabSortOrder'),
        },
        subPage: {
          subpage_name: t(
            'gaTracking.projectViewTab.manageColumnsAndSortOrder'
          ),
          subpage_type: GA_SUBPAGE_TYPE.DRAWER,
        },
      });
    }
  };

  const drawerContent = (
    <Box className="manage-field-sort-panel">
      <Box className="panel-header">
        <Box className="close-btn-container">
          <AurButton
            classNames={'drawer-close-button panel-close'}
            label={t('buttons.close')}
            type="exit"
            size="Medium"
            title={t('buttons.close')}
            onClick={() => {
              trackGAEvent({
                eventName: GA_EVENT_NAMES.CLICK,
                click: {
                  name: t('gaTracking.global.close'),
                  type: GA_CLICK_TYPES.BUTTON,
                  region: GA_REGIONS.DRAWER,
                  action: t('gaTracking.projectViewTab.closeDrawer'),
                },
                subPage: {
                  subpage_name: t(
                    'gaTracking.projectViewTab.manageColumnsAndSortOrder'
                  ),
                  subpage_type: GA_SUBPAGE_TYPE.DRAWER,
                },
              });
              handleClose();
            }}
          />
        </Box>
        <Box className="header-content">
          <Typography className="typo-text headline1_size title">
            {t('ecaDocList.manageFieldsSort.title')}
          </Typography>
          <Box>
            <AurButton
              classNames={'drawer-close-button panel-close'}
              label={t('buttons.dontSaveUpdate')}
              type="exit"
              size="Medium"
              title={t('buttons.dontSaveUpdate')}
              onClick={() => {
                trackGAEvent({
                  eventName: GA_EVENT_NAMES.CLICK,
                  click: {
                    name: t(
                      'gaTracking.projectViewTab.dontSaveManageColumnAndSortOrder'
                    ),
                    type: GA_CLICK_TYPES.BUTTON,
                    region: GA_REGIONS.DRAWER,
                    action: t(
                      'gaTracking.projectViewTab.canceledChangeSettings'
                    ),
                  },
                  subPage: {
                    subpage_name: t(
                      'gaTracking.projectViewTab.manageColumnsAndSortOrder'
                    ),
                    subpage_type: GA_SUBPAGE_TYPE.DRAWER,
                  },
                });
                handleClose();
              }}
            />
            <Button
              id="basic-button"
              onClick={handleSave}
              className="typo-text buttonMedium_size custom-menu-button custom-button"
              variant="contained"
            >
              {t('buttons.save')}
            </Button>
          </Box>
        </Box>
      </Box>
      <Box className="manageColSort-content">
        <Box className="tab-container">
          <AurTabMenu
            tabs={tabs}
            headerClasses="typo-text subhead4_size"
            isResponsive={true}
            width="100%"
            onTabChange={handleTabChange}
          />
        </Box>
      </Box>
    </Box>
  );

  return (
    <AurDrawer
      className="manage-field-sort-drawer"
      open={showDrawer}
      onClose={handleClose}
      anchor="right"
      drawerContent={drawerContent}
    />
  );
};

export default ManageColumnsDrawer;
